import type { ProviderInteractionMode, RuntimeMode } from "@threadlines/contracts";
import { memo, type ReactNode, useState } from "react";
import { EllipsisIcon } from "lucide-react";
import { Button } from "../ui/button";
import {
  Menu,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator as MenuDivider,
  MenuTrigger,
} from "../ui/menu";
import { interactionModeOptions } from "../../interactionModeOptions";
import type { RuntimeModeOption } from "../../runtimeModeOptions";

/**
 * Narrow-footer overflow menu for the mode, access, and trait controls. The
 * stash stays out of here on purpose: it keeps its own always-visible
 * bookmark button, so a phone user never has to dig through a nested menu
 * to park a prompt.
 */
export const CompactComposerControlsMenu = memo(function CompactComposerControlsMenu(props: {
  interactionMode: ProviderInteractionMode;
  runtimeMode: RuntimeMode;
  runtimeModeOptions: ReadonlyArray<RuntimeModeOption>;
  showInteractionModeToggle: boolean;
  traitsMenuContent?: ReactNode;
  onInteractionModeChange: (mode: ProviderInteractionMode) => void;
  onRuntimeModeChange: (mode: RuntimeMode) => void;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Menu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <MenuTrigger
        render={
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 px-2 text-muted-foreground/70 hover:text-foreground/80"
            aria-label="More composer controls"
          />
        }
      >
        <EllipsisIcon aria-hidden="true" className="size-4" />
      </MenuTrigger>
      <MenuPopup align="start">
        {props.traitsMenuContent ? (
          <>
            {props.traitsMenuContent}
            <MenuDivider />
          </>
        ) : null}
        {props.showInteractionModeToggle ? (
          <>
            <div className="px-2 py-1.5 font-medium text-muted-foreground text-xs">Mode</div>
            <MenuRadioGroup
              value={props.interactionMode}
              onValueChange={(value) => {
                if (!value || value === props.interactionMode) return;
                props.onInteractionModeChange(value as ProviderInteractionMode);
              }}
            >
              {interactionModeOptions.map((option) => {
                const OptionIcon = option.icon;
                return (
                  <MenuRadioItem key={option.mode} value={option.mode} title={option.description}>
                    <span className="inline-flex min-w-0 items-center gap-1.5">
                      <OptionIcon
                        aria-hidden="true"
                        className="size-3.5 shrink-0 text-muted-foreground"
                      />
                      <span className="truncate">{option.label}</span>
                    </span>
                  </MenuRadioItem>
                );
              })}
            </MenuRadioGroup>
            <MenuDivider />
          </>
        ) : null}
        <div className="px-2 py-1.5 font-medium text-muted-foreground text-xs">Access</div>
        <MenuRadioGroup
          value={props.runtimeMode}
          // Shown is the level the chat runs at, which can differ from the
          // one chosen: picking it pins it, so only an empty value is skipped.
          onValueChange={(value) => {
            if (!value) return;
            props.onRuntimeModeChange(value as RuntimeMode);
          }}
        >
          {props.runtimeModeOptions.map((option) => {
            const OptionIcon = option.icon;
            return (
              <MenuRadioItem
                key={option.mode}
                value={option.mode}
                disabled={option.disabled === true}
                title={
                  option.disabled && option.disabledReason
                    ? option.disabledReason
                    : option.description
                }
              >
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <OptionIcon
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-muted-foreground"
                  />
                  <span className="truncate">{option.label}</span>
                </span>
              </MenuRadioItem>
            );
          })}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
});
